import React from 'react';
import SlidePanel from '../SlidePanel';
import { useQueuePanel } from './QueuePanelContext';
import { AskRequest } from '@hone-automation/common';

// Import panel content components from the panels folder
import { InfoPanel, SummarizePanel } from '../../panels';

interface PanelData {
  request: AskRequest;
  trxURL?: string;
}

interface PanelState {
  isOpen: boolean;
  panelType: string | null;
  data: PanelData;
  title?: string;
  titleIcon?: React.ReactNode;
  showExplicitCloseButton?: boolean;
}

interface QueuePanelSystemProps {
  // Additional props if needed
}

/**
 * A system that manages all slide panels in the Queue component.
 * This component should be mounted at the Queue level to ensure panels
 * can slide over the entire Queue view.
 */
const QueuePanelSystem: React.FC<QueuePanelSystemProps> = () => {
  const { panelState, closePanel } = useQueuePanel();
  const { isOpen, panelType, data, title, titleIcon, showExplicitCloseButton } = panelState as {
    isOpen: boolean;
    panelType: string | null;
    data: PanelData;
    title?: string;
    titleIcon?: React.ReactNode;
    showExplicitCloseButton?: boolean;
  };

  // Render the appropriate panel content based on panelType
  const renderPanelContent = () => {
    if (!panelType || !data?.request) {
      return <div>No content available</div>;
    }

    const panelProps = {
      request: data.request,
      trxURL: data.trxURL,
    };

    switch (panelType) {
      case 'transactionDetails':
        return <InfoPanel {...panelProps} />;
      case 'summarizeRequest':
        return <SummarizePanel {...panelProps} />;
      default:
        return <div>No panel content found for type: {panelType}</div>;
    }
  };

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={closePanel}
      title={title || getPanelTitle(panelType)}
      titleIcon={titleIcon}
      showExplicitCloseButton={showExplicitCloseButton}
      showBetaBadge={panelType === 'summarizeRequest'}
      position="right"
      width="450px"
    >
      {renderPanelContent()}
    </SlidePanel>
  );
};

// Helper function to get panel title based on type
const getPanelTitle = (panelType: string | null): string => {
  switch (panelType) {
    case 'transactionDetails':
      return 'Transaction Details';
    case 'summarizeRequest':
      return 'Request Summary';
    default:
      return 'Panel';
  }
};

export default QueuePanelSystem;
